import type {ThemeBreakpoints} from '_uiComponents'

const getSidebarHeight = (headerSize?: ThemeBreakpoints) => ({
    lg: `calc(100vh - ${(headerSize?.lg ?? 56) + 24}px)`,
    xl: `calc(100vh - ${(headerSize?.xl ?? 56) + 24}px)`,
    '2xl': `calc(100vh - ${(headerSize?.['2xl'] ?? 56) + 24}px)`,
})

const stories = {
    Stories: {
        parts: [
            'wrapper',
            'sidebar',
            'pageTitle',
            'nav',
            'navLink',
            'sidebarDescription',
            'mobileTitle',
            'mobileDescription',
            'accordion',
            'accordionItem',
            'accordionButton',
            'accordionPanel',
            'content',
            'section',
            'subsection',
            'sectionTitle',
            'sectionSubtitle',
            'sectionCta',
            'grid',
            'card',
            'cardLink',
            'cardMedia',
            'cardMediaZoom',
            'cardTitle',
            'cardDescription',
        ],
        baseStyle: ({headerSize, headerSizeWithBanner, activeSection}: {headerSize?: ThemeBreakpoints; headerSizeWithBanner?: ThemeBreakpoints; activeSection?: boolean} = {}) => ({
            wrapper: {
                display: 'grid',
                gridTemplateColumns: {base: '1fr', lg: 'minmax(200px, 22%) 1fr'},
                columnGap: {lg: 'spacing.36'},
                rowGap: {base: 'spacing.8', lg: 0},
                w: '100%',
                px: {base: 'spacing.6', lg: 'spacing.6'},
                pt: {
                    base: `${(headerSize?.base ?? 0) + 24}px`,
                    sm: `${(headerSize?.sm ?? 0) + 24}px`,
                    md: `${(headerSize?.md ?? 0) + 24}px`,
                    lg: `${(headerSize?.lg ?? 0) + 24}px`,
                    xl: `${(headerSize?.xl ?? 0) + 24}px`,
                    '2xl': `${(headerSize?.['2xl'] ?? 0) + 24}px`,
                },
                pb: {base: 'spacing.10', lg: 'spacing.20'},
                pr: {base: 'spacing.6', lg: 'spacing.35'},
            },

            sidebar: {
                display: 'flex',
                flexDirection: 'column',
                gap: {base: 'spacing.4', lg: 'spacing.6'},
                position: {lg: 'sticky'},
                top: {lg: 'spacing.20'},
                alignSelf: 'start',
                // Sized against the expanded header plus banner (tallest state) so the description stays in the viewport at the top of the page too.
                maxH: getSidebarHeight(headerSizeWithBanner ?? headerSize),
                h: getSidebarHeight(headerSizeWithBanner ?? headerSize),
                pb: {lg: 'spacing.6'},
                overflowY: {lg: 'auto'},
            },
            pageTitle: {
                textStyle: 'displaySm',
                fontWeight: 400,
                color: 'color.text.black',
            },
            nav: {
                gap: {base: 'spacing.4', lg: 'spacing.4'},
            },
            navLink: {
                textStyle: 'paragraphSm',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'color.text.grey.600',
                textDecoration: 'none',
                w: 'fit-content',
                transition: 'color 300ms ease',

                '&[data-active="true"]': {
                    color: 'color.text.black',
                    textDecoration: 'underline',
                    textUnderlineOffset: '4px',
                },

                _hover: {
                    color: 'color.text.black',
                    textDecoration: 'none',
                },
            },
            sidebarDescription: {
                textStyle: 'paragraphSm',
                color: 'color.text.grey.600',
                mb: {base: 'spacing.2'},
            },

            // Sidebar heading on mobile: shows the active section title (centered) or the page title.
            mobileTitle: {
                textStyle: 'displaySm',
                fontWeight: 400,
                color: 'color.text.black',
                w: '100%',
                textAlign: 'left',
            },
            mobileDescription: {
                textStyle: 'paragraphSm',
                color: 'color.text.grey.600',
            },
            accordion: {
                w: '100%',
            },
            accordionItem: {
                borderTopWidth: '0px',
                borderBottomWidth: '1px',
                borderColor: 'color.border.grey.200',
                py: 'spacing.4',
            },
            accordionButton: {
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'color.text.black',
                px: 0,
                py: 0,
                span: {
                    textStyle: 'buttonSm',
                },

                _hover: {
                    bg: 'transparent',
                },
            },
            accordionPanel: {
                px: 0,
                py: 0,
            },

            content: {
                display: 'flex',
                flexDirection: 'column',
                gap: {base: 'spacing.10', lg: 'spacing.16'},
                minW: 0,

                // Replayed on every route change because the content column is keyed on the active section.
                '@keyframes storiesFadeIn': {
                    from: {opacity: 0, transform: 'translateY(12px)'},
                    to: {opacity: 1, transform: 'translateY(0)'},
                },
                animation: 'storiesFadeIn 400ms cubic-bezier(0.25, 0.46, 0.45, 0.94) both',

                '@media (prefers-reduced-motion: reduce)': {
                    animation: 'none',
                },
            },

            section: {
                display: 'flex',
                flexDirection: 'column',
                gap: {base: 'spacing.4', lg: 'spacing.6'},
            },
            subsection: {
                display: 'flex',
                flexDirection: 'column',
                gap: {base: 'spacing.4', lg: 'spacing.6'},
                mt: {base: 'spacing.8', lg: 'spacing.12'},
            },
            sectionTitle: {
                textStyle: {base: 'paragraphXl', lg: 'displaySm'},
                fontWeight: {base: 400, lg: 350},
                color: 'color.text.black',
            },
            subsectionTitle: {
                textStyle: {base: 'paragraphXl', lg: 'paragraphXl'},
                fontWeight: {base: 400},
                color: 'color.text.black',
            },
            sectionSubtitle: {
                textStyle: 'paragraphSm',
                color: 'color.text.grey.600',
                mt: '-spacing.2',
                pb: {base: 'spacing.2', lg: 'unset'},
            },
            sectionCta: {
                textStyle: 'buttonSm',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'color.text.black',
                alignSelf: 'center',
                textDecoration: 'underline',
                textUnderlineOffset: '4px',
                mt: {base: 'spacing.4', lg: 'spacing.6'},
                transition: 'text-underline-offset 300ms ease, opacity 300ms ease',

                _hover: {
                    textDecoration: 'underline',
                    textUnderlineOffset: '6px',
                    opacity: 0.7,
                },
            },

            grid: {
                w: '100%',
                gap: {base: 'spacing.2', lg: 'spacing.6'},
                rowGap: {base: 'spacing.4', lg: 'spacing.6'},
            },

            card: {
                display: 'flex',
                flexDirection: 'column',
                gap: {base: 'spacing.1'},
                minW: 0,
            },
            cardLink: {
                display: 'flex',
                flexDirection: 'column',
                gap: {base: 'spacing.1'},
                textDecoration: 'none',

                _hover: {
                    textDecoration: 'none',
                },
            },
            cardMedia: {
                w: '100%',
                overflow: 'hidden',

                '& > div, & picture': {
                    display: 'block',
                    w: '100%',
                    h: '100%',
                },
                '& img': {
                    w: '100%',
                    h: '100%',
                    objectFit: 'cover',
                    transition: 'transform 600ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                },
            },
            cardMediaZoom: {
                '& img': {
                    transition: 'transform 0.4s ease',
                },
                '&:hover img': {
                    transform: 'scale(1.05)',
                },
                '@media (prefers-reduced-motion: reduce)': {
                    '& img': {
                        transition: 'none',
                    },
                    '&:hover img': {
                        transform: 'none',
                    },
                },
            },
            cardTitle: {
                textStyle: 'paragraphSm',
                color: 'color.text.black',
                fontWeight: '400',
                pt: {base: 'spacing.1'},
            },
            cardDescription: {
                textStyle: 'paragraphXs',
                color: 'color.text.black',
                pb: {base: 'spacing.2'},
            },
        }),
    },
}

export default stories
